import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteAttachment } from "../../hooks/useTickets";
import { showToast } from "../../lib/toast";
import { getErrorMessage } from "../../lib/apiErrors";
import type { TicketAttachmentResponse } from "../../types/tickets";

interface AttachmentDeleteDialogProps {
  ticketId: string;
  // The attachment to delete; the dialog is open while it's set
  attachment: TicketAttachmentResponse | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirms permanently deleting an attachment (admins of the ticket's organization). The ticket's history records
 * that it was deleted.
 */
export const AttachmentDeleteDialog: React.FC<AttachmentDeleteDialogProps> = ({
  ticketId,
  attachment,
  onOpenChange,
}) => {
  const deleteAttachmentMutation = useDeleteAttachment();

  const handleDelete = async () => {
    if (!attachment) return;
    try {
      await deleteAttachmentMutation.mutateAsync({ attachmentId: attachment.id, ticketId });
      showToast.success("Attachment Deleted", `${attachment.fileName} was permanently deleted.`);
      onOpenChange(false);
    } catch (error) {
      showToast.error("Delete Failed", getErrorMessage(error, "Failed to delete the attachment. Please try again."));
    }
  };

  return (
    <AlertDialog open={attachment !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {attachment?.fileName}?</AlertDialogTitle>
          <AlertDialogDescription>
            The file is permanently deleted for everyone, including the customer. The ticket's history records that
            it was deleted. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            disabled={deleteAttachmentMutation.isPending}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleteAttachmentMutation.isPending ? "Deleting..." : "Delete Attachment"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
